import { useState, useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import "./Calendar.scss";

const DAYS_SK = ["Po", "Ut", "St", "Št", "Pi", "So", "Ne"];
const MONTHS_SK = [
  "Január",
  "Február",
  "Marec",
  "Apríl",
  "Máj",
  "Jún",
  "Júl",
  "August",
  "September",
  "Október",
  "November",
  "December",
];

function Calendar({
  events = [],
  selectedDate,
  onDateSelect,
  onEventClick,
  onNext,
  onPrevious,
  view: controlledView,
  className = "",
}) {
  const [viewDate, setViewDate] = useState(() => {
    if (selectedDate) return new Date(selectedDate);
    return new Date();
  });
  const [internalView, setInternalView] = useState("month");
  const view = controlledView || internalView;

  const year = viewDate.getFullYear();
  const month = viewDate.getMonth();

  const days = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    let startDow = (firstDay.getDay() + 6) % 7;
    const result = [];

    for (let i = startDow - 1; i >= 0; i--) {
      result.push({ date: new Date(year, month, -i), outside: true });
    }
    for (let i = 1; i <= lastDay.getDate(); i++) {
      result.push({ date: new Date(year, month, i), outside: false });
    }
    const remaining = 42 - result.length;
    for (let i = 1; i <= remaining; i++) {
      result.push({ date: new Date(year, month + 1, i), outside: true });
    }
    return result;
  }, [year, month]);

  const getEventsForDate = (date) => {
    return events.filter((ev) => {
      const evDate = new Date(ev.date);
      return evDate.toDateString() === date.toDateString();
    });
  };

  const isToday = (date) => date.toDateString() === new Date().toDateString();
  const isSelected = (date) =>
    selectedDate &&
    date.toDateString() === new Date(selectedDate).toDateString();

  const handlePrev = () => {
    const from =
      view === "month"
        ? new Date(year, month - 1, 1)
        : new Date(year, month, viewDate.getDate() - 7);
    const to =
      view === "month"
        ? new Date(year, month, 0)
        : new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
    setViewDate(from);
    onPrevious?.({ from, to });
  };

  const handleNext = () => {
    const from =
      view === "month"
        ? new Date(year, month + 1, 1)
        : new Date(year, month, viewDate.getDate() + 7);
    const to =
      view === "month"
        ? new Date(year, month + 2, 0)
        : new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
    setViewDate(from);
    onNext?.({ from, to });
  };

  const handleToday = () => {
    const from =
      view === "month"
        ? new Date(new Date().getFullYear(), new Date().getMonth(), 1)
        : new Date(year, month, viewDate.getDate() + 7);
    const to =
      view === "month"
        ? new Date(new Date().getFullYear(), new Date().getMonth() + 1, 0)
        : new Date(from.getFullYear(), from.getMonth(), from.getDate() + 6);
    onNext?.({ from, to });
    setViewDate(new Date());
    onDateSelect?.(new Date());
  };

  const handleDayClick = (date, outside) => {
    if (outside) return;
    onDateSelect?.(date);
  };

  // Week view: get current week days
  const weekDays = useMemo(() => {
    const startOfWeek = new Date(viewDate);
    const dow = (startOfWeek.getDay() + 6) % 7;
    startOfWeek.setDate(startOfWeek.getDate() - dow);
    const result = [];
    for (let i = 0; i < 7; i++) {
      const d = new Date(startOfWeek);
      d.setDate(d.getDate() + i);
      result.push(d);
    }
    return result;
  }, [viewDate]);

  const weekEvents = useMemo(() => {
    if (view !== "week") return [];
    return weekDays
      .flatMap((d) => getEventsForDate(d).map((ev) => ({ ...ev, dayDate: d })))
      .sort((a, b) => {
        if (a.timeFrom && b.timeFrom)
          return a.timeFrom.localeCompare(b.timeFrom);
        return 0;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view, weekDays, events]);

  // Events for selected date (shown below calendar)
  const selectedDateEvents = useMemo(() => {
    if (!selectedDate) return [];
    return getEventsForDate(new Date(selectedDate));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate, events]);

  return (
    <div className={`calendar ${className}`}>
      {/* ── Header ── */}
      <div className="calendar__header">
        <div className="calendar__header-left">
          <h2 className="calendar__title">
            {view === "month"
              ? `${MONTHS_SK[month]} ${year}`
              : `${weekDays[0].getDate()}. ${weekDays[0].getMonth() + 1}. – ${weekDays[6].getDate()}. ${weekDays[6].getMonth() + 1}. ${year}`}
          </h2>
          <button className="calendar__today-btn" onClick={handleToday}>
            Dnes
          </button>
        </div>

        <div className="calendar__header-right">
          {/* {!controlledView && (
            <div className="calendar__view-toggle">
              <button
                className={`calendar__view-btn ${view === "month" ? "calendar__view-btn--active" : ""}`}
                onClick={() => setInternalView("month")}
              >
                Mesiac
              </button>
              <button
                className={`calendar__view-btn ${view === "week" ? "calendar__view-btn--active" : ""}`}
                onClick={() => setInternalView("week")}
              >
                Týždeň
              </button>
            </div>
          )} */}

          <div className="calendar__nav">
            <button
              className="calendar__nav-btn"
              onClick={handlePrev}
              aria-label="Predchádzajúci"
            >
              <ChevronLeft size={18} strokeWidth={2} />
            </button>
            <button
              className="calendar__nav-btn"
              onClick={handleNext}
              aria-label="Nasledujúci"
            >
              <ChevronRight size={18} strokeWidth={2} />
            </button>
          </div>
        </div>
      </div>

      {/* ── Month view ── */}
      {view === "month" && (
        <div className="calendar__grid">
          <div className="calendar__weekdays">
            {DAYS_SK.map((d) => (
              <span key={d} className="calendar__weekday">
                {d}
              </span>
            ))}
          </div>

          <div className="calendar__days">
            {days.map(({ date, outside }, i) => {
              const dayEvents = getEventsForDate(date);
              const today = isToday(date);
              const selected = isSelected(date);
              return (
                <button
                  key={i}
                  type="button"
                  className={[
                    "calendar__day",
                    outside && "calendar__day--outside",
                    today && "calendar__day--today",
                    selected && "calendar__day--selected",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleDayClick(date, outside)}
                >
                  <span className="calendar__day-number">{date.getDate()}</span>
                  {dayEvents.length > 0 && !outside && (
                    <div className="calendar__day-dots">
                      {dayEvents.slice(0, 3).map((_, j) => (
                        <span key={j} className="calendar__day-dot" />
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── Week view ── */}
      {view === "week" && (
        <>
          <div className="calendar__week-header">
            {weekDays.map((d, i) => {
              const today = isToday(d);
              const selected = isSelected(d);
              return (
                <button
                  key={i}
                  type="button"
                  className={[
                    "calendar__week-day",
                    today && "calendar__week-day--today",
                    selected && "calendar__week-day--selected",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  onClick={() => handleDayClick(d, false)}
                >
                  <span className="calendar__week-day-label">{DAYS_SK[i]}</span>
                  <span className="calendar__week-day-number">
                    {d.getDate()}
                  </span>
                </button>
              );
            })}
          </div>

          {weekEvents.length > 0 && (
            <div className="calendar__events-list">
              {weekEvents.map((ev, i) => (
                <div
                  key={i}
                  className="calendar__event-item"
                  onClick={() => onEventClick?.(ev)}
                  role={onEventClick ? "button" : undefined}
                  tabIndex={onEventClick ? 0 : undefined}
                >
                  <span
                    className="calendar__event-dot"
                    style={ev.color ? { backgroundColor: ev.color } : {}}
                  />
                  <div className="calendar__event-body">
                    <span className="calendar__event-title">{ev.title}</span>
                    <span className="calendar__event-description">
                      {ev.description ?? ""}
                    </span>
                    <span className="calendar__event-time">
                      {ev.timeFrom || "—"} – {ev.timeTo || "—"}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </>
      )}

      {/* ── Selected date events ── */}
      {selectedDate && selectedDateEvents.length > 0 && view === "month" && (
        <div className="calendar__selected-events">
          <h3 className="calendar__selected-title">
            {new Date(selectedDate).getDate()}.{" "}
            {MONTHS_SK[new Date(selectedDate).getMonth()]}
          </h3>
          <div className="calendar__selected-list">
            {selectedDateEvents.map((ev, i) => (
              <div
                key={i}
                className="calendar__selected-item"
                onClick={() => onEventClick?.(ev)}
                role={onEventClick ? "button" : undefined}
                tabIndex={onEventClick ? 0 : undefined}
              >
                <span
                  className="calendar__selected-dot"
                  style={ev.color ? { backgroundColor: ev.color } : {}}
                />
                <div className="calendar__selected-body">
                  <span className="calendar__selected-event-title">
                    {ev.title}
                  </span>
                  <span className="calendar__event-description">
                    {ev.description ?? ""}
                  </span>
                  <span className="calendar__event-time">
                    {ev.timeFrom || "—"} – {ev.timeTo || "—"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default Calendar;
